import React, { useMemo, useState } from 'react';
import { accountingApi } from '../../services/accountingApi';

const LABELS = {
    rucs: 'RUC',
    banks: 'Bancos',
    rucBanks: 'Bancos por RUC',
    suppliers: 'Proveedores',
    areas: 'Áreas',
    categories: 'Categorías',
    externalRequesters: 'Solicitantes externos',
    paymentMethods: 'Métodos de pago',
    receiptTypes: 'Comprobantes',
};

const emptyByType = {
    rucs: { business_name: '', ruc: '', alias: '', is_active: 1 },
    banks: { name: '', short_name: '', is_active: 1 },
    rucBanks: { ruc_id: '', bank_id: '', account_number: '', cci: '', currency: 'PEN', alias: '', is_active: 1 },
    suppliers: { business_name: '', ruc: '', phone: '', bank: '', account_number: '', is_active: 1 },
    areas: { name: '', color: '#6f42c1', is_active: 1 },
    categories: { area_id: '', operation_type: 'EGRESO', name: '', description: '', is_tax: 0, is_active: 1 },
    externalRequesters: { full_name: '', area_id: '', phone: '', is_active: 1 },
    paymentMethods: { name: '', requires_reference: 0, is_active: 1 },
    receiptTypes: { name: '', requires_number: 0, is_active: 1 },
};

export default function CatalogCrud({ type, meta, reload }) {
    const [form, setForm] = useState(emptyByType[type] || {});
    const [saving, setSaving] = useState(false);
    const canEdit = meta.permissions?.canEdit !== false;

    const rows = useMemo(() => {
        if (type === 'rucBanks') return meta.rucBanks || [];
        return meta[type] || [];
    }, [meta, type]);

    const change = (name, value) => setForm((p) => ({ ...p, [name]: value }));

    const save = async () => {
        if (!canEdit) return alert('El rol 8 no puede crear, editar ni eliminar configuración contable.');
        setSaving(true);
        try {
            await accountingApi.saveCatalog(type, form);
            setForm(emptyByType[type] || {});
            await reload();
        } catch (e) {
            alert(e.response?.data?.message || 'No se pudo guardar.');
        } finally {
            setSaving(false);
        }
    };

    const edit = (row) => {
        if (!canEdit) return;
        setForm({ ...emptyByType[type], ...row });
    };

    const remove = async (id) => {
        if (!canEdit) return alert('El rol 8 no puede eliminar configuración contable.');
        if (!window.confirm('¿Seguro que deseas eliminar este registro?')) return;
        await accountingApi.deleteCatalog(type, id);
        await reload();
    };

    const input = (name, placeholder, extra = {}) => (
        <input
            value={form[name] ?? ''}
            placeholder={placeholder}
            disabled={!canEdit}
            onChange={(e) => change(name, e.target.value)}
            {...extra}
        />
    );

    const renderFields = () => {
        if (type === 'rucs') return <>{input('business_name', 'Razón social')}{input('ruc', 'RUC')}{input('alias', 'Alias')}</>;
        if (type === 'banks') return <>{input('name', 'Banco: BCP, BBVA...')}{input('short_name', 'Nombre corto')}</>;
        if (type === 'rucBanks') return <>
            <select value={form.ruc_id || ''} disabled={!canEdit} onChange={(e) => change('ruc_id', e.target.value)}><option value="">RUC</option>{(meta.rucs || []).map((x) => <option key={x.id} value={x.id}>{x.alias || x.business_name}</option>)}</select>
            <select value={form.bank_id || ''} disabled={!canEdit} onChange={(e) => change('bank_id', e.target.value)}><option value="">Banco</option>{(meta.banks || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
            {input('account_number', 'Número de cuenta')}{input('cci', 'CCI')}
            <select value={form.currency || 'PEN'} disabled={!canEdit} onChange={(e) => change('currency', e.target.value)}><option value="PEN">Soles</option><option value="USD">Dólares</option></select>
            {input('alias', 'Alias de cuenta')}
        </>;
        if (type === 'suppliers') return <>{input('business_name', 'Proveedor')}{input('ruc', 'RUC')}{input('phone', 'Teléfono')}{input('bank', 'Banco')}{input('account_number', 'Cuenta')}</>;
        if (type === 'areas') return <>{input('name', 'Área')}{input('color', 'Color', { type: 'color' })}</>;
        if (type === 'categories') return <>
            <select value={form.area_id || ''} disabled={!canEdit} onChange={(e) => change('area_id', e.target.value)}><option value="">Área</option>{(meta.areas || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
            <select value={form.operation_type || 'EGRESO'} disabled={!canEdit} onChange={(e) => change('operation_type', e.target.value)}><option value="EGRESO">Egreso</option><option value="INGRESO">Ingreso</option></select>
            {input('name', 'Categoría')}{input('description', 'Descripción')}
        </>;
        if (type === 'externalRequesters') return <>{input('full_name', 'Nombre completo')}<select value={form.area_id || ''} disabled={!canEdit} onChange={(e) => change('area_id', e.target.value)}><option value="">Área</option>{(meta.areas || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>{input('phone', 'Teléfono')}</>;
        if (type === 'paymentMethods') return <>{input('name', 'Método')}<label><input type="checkbox" checked={Number(form.requires_reference) === 1} disabled={!canEdit} onChange={(e) => change('requires_reference', e.target.checked ? 1 : 0)} /> Requiere referencia</label></>;
        if (type === 'receiptTypes') return <>{input('name', 'Comprobante')}<label><input type="checkbox" checked={Number(form.requires_number) === 1} disabled={!canEdit} onChange={(e) => change('requires_number', e.target.checked ? 1 : 0)} /> Requiere número</label></>;
        return null;
    };

    const rowLabel = (x) => x.label || x.business_name || x.name || x.full_name || x.alias || `Registro ${x.id}`;

    return (
        <section className="accCard catalogCrud">
            <div className="catalogHeader"><h3>{LABELS[type] || type}</h3>{form.id && <button type="button" onClick={() => setForm(emptyByType[type])}>Nuevo</button>}</div>
            <div className="catalogForm">{renderFields()}<button type="button" disabled={!canEdit || saving} onClick={save}>{form.id ? 'Actualizar' : 'Agregar'}</button></div>
            <div className="catalogRows">
                {rows.map((x) => <div className="catalogRow" key={x.id}><span>{rowLabel(x)}</span><div>{canEdit && <button type="button" onClick={() => edit(x)}>Editar</button>}{canEdit && <button type="button" onClick={() => remove(x.id)}>Eliminar</button>}</div></div>)}
            </div>
        </section>
    );
}
